import { useState, useEffect } from 'react';
import { useStore } from '@/infra/store';
import { randomSide, resolveGuess, guessReward } from '@/domain/minigame/guessGame';
import { resolveRps, randomRpsPick, rpsReward } from '@/domain/minigame/rpsGame';
import { createBoard, flipCard, checkMatch, isComplete, memoryReward } from '@/domain/minigame/memoryGame';
import { isCooldownActive, cooldownRemaining } from '@/domain/minigame/cooldown';
import type { GuessSide, RpsPick, MemoryCard, MiniGameResult } from '@/domain/minigame/types';
import { useT } from '@/infra/i18n';
import { COLORS, FONT, SPACING, RADIUS } from '@/presentation/theme/tokens';
import { overlayStyle, cardStyle } from '@/presentation/modals/modal-shared';
import { PixelIcon, type PixelIconName } from '@/presentation/icons/PixelIcon';

type Screen = 'menu' | 'guess' | 'rps' | 'memory';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function MiniGameOverlay({ open, onClose }: Props) {
  const t = useT();
  const [screen, setScreen] = useState<Screen>('menu');
  const lastAt = useStore((s) => s.lastMiniGameAt);
  const setLastAt = useStore((s) => s.setLastMiniGameAt);

  useEffect(() => {
    if (open) setScreen('menu');
  }, [open]);

  if (!open) return null;
  const now = Date.now();
  const onCd = isCooldownActive(lastAt, now);
  const remain = cooldownRemaining(lastAt, now);

  const finish = (reward: number): void => {
    if (reward > 0) {
      const c = useStore.getState().creature;
      if (c) {
        useStore.getState().replaceCreature({
          ...c,
          state: { ...c.state, happiness: Math.min(100, c.state.happiness + reward) },
        });
      }
    }
    setLastAt(Date.now());
    setTimeout(() => onClose(), 1500);
  };

  return (
    <div role="dialog" data-testid="minigame-overlay" style={overlayStyle} onClick={onClose}>
      <div style={{ ...cardStyle, maxWidth: 360 }} onClick={(e) => e.stopPropagation()}>
        {screen === 'menu' && (
          <Menu t={t} onPick={setScreen} onClose={onClose} cooldown={onCd} remain={remain} />
        )}
        {screen === 'guess' && <Guess t={t} onFinish={finish} />}
        {screen === 'rps' && <Rps t={t} onFinish={finish} />}
        {screen === 'memory' && <Memory t={t} onFinish={finish} />}
      </div>
    </div>
  );
}

function Menu({
  t,
  onPick,
  onClose,
  cooldown,
  remain,
}: {
  t: ReturnType<typeof useT>;
  onPick: (s: Screen) => void;
  onClose: () => void;
  cooldown: boolean;
  remain: number;
}) {
  const fmt = (ms: number): string => {
    const s = Math.ceil(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };
  return (
    <>
      <Title icon="minigame">{t('minigame.title')}</Title>
      {cooldown && (
        <p style={{ color: COLORS.danger, fontSize: FONT.sizeSm, marginBottom: SPACING.sm }}>
          {t('minigame.cooldown', { time: fmt(remain) })}
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
        <MenuButton testId="mg-pick-guess" disabled={cooldown} icon="book" onClick={() => onPick('guess')}>
          {t('minigame.guess')}
        </MenuButton>
        <MenuButton testId="mg-pick-rps" disabled={cooldown} icon="sword" onClick={() => onPick('rps')}>
          {t('minigame.rps')}
        </MenuButton>
        <MenuButton testId="mg-pick-memory" disabled={cooldown} icon="crystal" onClick={() => onPick('memory')}>
          {t('minigame.memory')}
        </MenuButton>
      </div>
      <button data-testid="mg-close" onClick={onClose} style={{ ...secondaryBtn, marginTop: SPACING.md }}>
        {t('minigame.close')}
      </button>
    </>
  );
}

function MenuButton({
  testId,
  disabled,
  icon,
  onClick,
  children,
}: {
  testId: string;
  disabled: boolean;
  icon: PixelIconName;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button data-testid={testId} disabled={disabled} onClick={onClick} style={listBtn(disabled)}>
      <PixelIcon name={icon} size={22} color="#ffd6b3" />
      <span>{children}</span>
    </button>
  );
}

function Guess({ t, onFinish }: { t: ReturnType<typeof useT>; onFinish: (reward: number) => void }) {
  const [hidden] = useState<GuessSide>(randomSide);
  const [result, setResult] = useState<MiniGameResult | null>(null);
  const pick = (side: GuessSide): void => {
    if (result) return;
    const r = resolveGuess(side, hidden);
    setResult(r);
    onFinish(guessReward(r));
  };
  return (
    <>
      <Title icon="book">{t('minigame.guess')}</Title>
      <p style={{ color: COLORS.textDim, fontSize: FONT.sizeSm }}>{t('minigame.guess.instruction')}</p>
      {result === null ? (
        <div style={{ display: 'flex', justifyContent: 'center', gap: SPACING.lg, marginTop: SPACING.md }}>
          <button data-testid="mg-guess-left" style={choiceBtn} onClick={() => pick('left')}>
            LEFT
          </button>
          <button data-testid="mg-guess-right" style={choiceBtn} onClick={() => pick('right')}>
            RIGHT
          </button>
        </div>
      ) : (
        <p data-testid="mg-result" style={resultStyle}>
          {t(result === 'win' ? 'minigame.guess.win' : 'minigame.guess.lose')}
        </p>
      )}
    </>
  );
}

function Rps({ t, onFinish }: { t: ReturnType<typeof useT>; onFinish: (reward: number) => void }) {
  const [result, setResult] = useState<{ outcome: MiniGameResult; player: RpsPick; cpu: RpsPick } | null>(null);
  const pick = (player: RpsPick): void => {
    if (result) return;
    const cpu = randomRpsPick();
    const outcome = resolveRps(player, cpu);
    setResult({ outcome, player, cpu });
    onFinish(rpsReward(outcome));
  };
  const label: Record<RpsPick, string> = { rock: 'ROCK', paper: 'PAPER', scissors: 'CUT' };
  return (
    <>
      <Title icon="sword">{t('minigame.rps')}</Title>
      {result === null ? (
        <div style={{ display: 'flex', justifyContent: 'center', gap: SPACING.md, marginTop: SPACING.md }}>
          {(['rock', 'paper', 'scissors'] as RpsPick[]).map((p) => (
            <button key={p} data-testid={`mg-rps-${p}`} style={choiceBtn} onClick={() => pick(p)}>
              {label[p]}
            </button>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', marginTop: SPACING.md }}>
          <p style={{ ...resultStyle, marginBottom: SPACING.xs }}>
            {label[result.player]} vs {label[result.cpu]}
          </p>
          <p data-testid="mg-result" style={resultStyle}>
            {t(`minigame.rps.${result.outcome}` as 'minigame.rps.win')}
          </p>
        </div>
      )}
    </>
  );
}

function Memory({ t, onFinish }: { t: ReturnType<typeof useT>; onFinish: (reward: number) => void }) {
  const [board, setBoard] = useState<MemoryCard[]>(() => createBoard());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const flip = (id: number): void => {
    if (busy || done) return;
    const card = board.find((c) => c.id === id);
    if (!card || card.revealed || card.matched) return;
    const flipped = flipCard(board, id);
    setBoard(flipped);
    if (flipped.filter((c) => c.revealed && !c.matched).length === 2) {
      setBusy(true);
      const { board: checked, matched } = checkMatch(flipped);
      if (matched) {
        setBoard(checked);
        setBusy(false);
        if (isComplete(checked)) {
          setDone(true);
          onFinish(memoryReward());
        }
      } else {
        setTimeout(() => {
          setBoard(checked);
          setBusy(false);
        }, 700);
      }
    }
  };

  return (
    <>
      <Title icon="crystal">{t('minigame.memory')}</Title>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, maxWidth: 220, margin: '12px auto' }}>
        {board.map((c) => (
          <button
            key={c.id}
            data-testid={`mg-mem-${c.id}`}
            onClick={() => flip(c.id)}
            disabled={busy || c.matched}
            style={{
              ...cardBtn,
              background: c.matched
                ? 'rgba(155,255,155,0.2)'
                : c.revealed
                  ? COLORS.surfaceAlt
                  : COLORS.surface,
              cursor: c.matched || c.revealed ? 'default' : 'pointer',
            }}
          >
            {c.revealed || c.matched ? (
              <PixelIcon name={c.symbol as PixelIconName} size={26} color="#ffd6b3" />
            ) : (
              '?'
            )}
          </button>
        ))}
      </div>
      {done && (
        <p data-testid="mg-result" style={resultStyle}>
          {t('minigame.memory.complete')}
        </p>
      )}
    </>
  );
}

function Title({ icon, children }: { icon: PixelIconName; children: React.ReactNode }) {
  return (
    <h2 style={title}>
      <PixelIcon name={icon} size={26} color="#ffd6b3" />
      {children}
    </h2>
  );
}

const title: React.CSSProperties = {
  margin: `0 0 ${SPACING.sm}px 0`,
  color: COLORS.accent,
  fontSize: FONT.sizeLg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: SPACING.xs,
};
const resultStyle: React.CSSProperties = {
  fontSize: FONT.sizeLg,
  textAlign: 'center',
  margin: SPACING.md,
  color: COLORS.text,
};
const listBtn = (disabled: boolean): React.CSSProperties => ({
  background: disabled ? 'rgba(40,40,50,0.5)' : COLORS.surfaceAlt,
  color: disabled ? COLORS.textFaint : COLORS.text,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.md,
  padding: `${SPACING.sm + 2}px ${SPACING.md}px`,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: FONT.sizeMd,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: SPACING.xs,
});
const choiceBtn: React.CSSProperties = {
  background: COLORS.surfaceAlt,
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.md,
  padding: `${SPACING.md}px ${SPACING.md}px`,
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
  fontFamily: FONT.mono,
  fontWeight: 700,
  minWidth: 72,
};
const cardBtn: React.CSSProperties = {
  ...choiceBtn,
  width: 44,
  height: 44,
  minWidth: 0,
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: FONT.sizeLg,
};
const secondaryBtn: React.CSSProperties = {
  background: 'transparent',
  color: COLORS.textDim,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  padding: '6px 14px',
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
  width: '100%',
};
